import { useState } from "react";
import "./SubmittedRuleview.css";
import { useNavigate } from "react-router-dom";


const DEFAULT_RULE = {
  id: "RUL-1042",
  title: "High-Risk Pregnancy Aspirin Prophylaxis",
  description: "High-Risk Pregnancy Aspirin Prophylaxis",
  createdOn: "Oct 24, 2023",
  createdBy: "Zoe H",
  status: "submitted",
  pathwayEntry: "OB/GYN Intake",
  triggerCodes: ["O09.89", "Z33.1"],
  configuredGuidance: "Consider Aspirin Prophylaxis for High-Risk Pregnancy.",
  recommendedAction: "Review and apply the recommended clinical guidance.",
  testingInstructions:
    "Verify that this rule triggers correctly in the application based on its configured pathway entry point and trigger codes.",
};

const STATUS_LABEL = {
  submitted: "Submitted for Testing",
  passed: "Passed",
  failed: "Failed",
};

function StatusText({ status }) {
  return (
    <span className={`ruleDetail-status ruleDetail-status--${status}`}>
      {STATUS_LABEL[status] || status}
    </span>
  );
}

export default function SubmittedRuleview({
  rule = DEFAULT_RULE,
  onBack,
  onFail,
  onApprove,
}) {
  const [busy, setBusy] = useState(null); // "fail" | "approve" | null
  const navigate = useNavigate();

  const handleFail = async () => {
    if (!onFail) return;
    setBusy("fail");
    try {
      await onFail(rule);
    } finally {
      setBusy(null);
    }
  };

  const handleApprove = async () => {
    if (!onApprove) return;
    setBusy("approve");
    try {
      await onApprove(rule);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="ruleDetail-page">
      {/* Header */}
      <div className="ruleDetail-header">
        <div className="ruleDetail-header-title-row">
          <h1 className="ruleDetail-title">{rule.title}</h1>
          <span className="ruleDetail-id-pill">{rule.id}</span>
          <span className="ruleDetail-header-divider" aria-hidden="true" />
          <StatusText status={rule.status} />
        </div>
      </div>

      {/* Rule Information */}
      <section className="ruleDetail-card">
        <div className="ruleDetail-card-header">Rule Information</div>
        <div className="ruleDetail-card-body">
          <div className="ruleDetail-grid">
            <div className="ruleDetail-field">
              <div className="ruleDetail-label">Rule ID</div>
              <div className="ruleDetail-value">{rule.id}</div>
            </div>
            <div className="ruleDetail-field">
              <div className="ruleDetail-label">Rule Description</div>
              <div className="ruleDetail-value">{rule.description}</div>
            </div>

            <div className="ruleDetail-field">
              <div className="ruleDetail-label">Created On</div>
              <div className="ruleDetail-value">{rule.createdOn}</div>
            </div>
            <div className="ruleDetail-field">
              <div className="ruleDetail-label">Created By</div>
              <div className="ruleDetail-value">{rule.createdBy}</div>
            </div>

            <div className="ruleDetail-field">
              <div className="ruleDetail-label">Status</div>
              <div className="ruleDetail-value">
                {STATUS_LABEL[rule.status] || rule.status}
              </div>
            </div>
            <div className="ruleDetail-field">
              <div className="ruleDetail-label">Pathway Entry Point</div>
              <div className="ruleDetail-value">{rule.pathwayEntry}</div>
            </div>

            <div className="ruleDetail-field ruleDetail-field--full">
              <div className="ruleDetail-label">Trigger Codes</div>
              <div className="ruleDetail-tags">
                {(rule.triggerCodes || []).map((code) => (
                  <span className="ruleDetail-tag" key={code}>
                    {code}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Guidance & Decision Support */}
      <section className="ruleDetail-card">
        <div className="ruleDetail-card-header">
          Guidance &amp; Decision Support
        </div>
        <div className="ruleDetail-card-body">
          <div className="ruleDetail-label">Configured Guidance</div>
          <div className="ruleDetail-guidance-box">
            &ldquo;{rule.configuredGuidance}&rdquo;
          </div>

          <div className="ruleDetail-label ruleDetail-label--spaced">
            Recommended Action
          </div>
          <div className="ruleDetail-value">{rule.recommendedAction}</div>
        </div>
      </section>

      {/* Testing */}
      <section className="ruleDetail-card">
        <div className="ruleDetail-card-header">Testing</div>
        <div className="ruleDetail-card-body">
          <p className="ruleDetail-testing-text">{rule.testingInstructions}</p>
        </div>
      </section>

      {/* Footer actions */}
      <div className="ruleDetail-footer">
        <button
          type="button"
          className="ruleDetail-btn ruleDetail-btn--ghost"
          onClick={onBack}
        >
          Back
        </button>
        <div className="ruleDetail-footer-actions">
          <button
            type="button"
            className="ruleDetail-btn ruleDetail-btn--danger"
            onClick={handleFail}
            disabled={busy !== null}
          >
            {busy === "fail" ? "Failing…" : "Fail Rule"}
          </button>
          <button
            type="button"
            className="ruleDetail-btn ruleDetail-btn--primary"
            onClick={handleApprove}
            disabled={busy !== null}
          >
            {busy === "approve" ? "Approving…" : "Approve Rule"}
          </button>
        </div>
      </div>
    </div>
  );
}